import type { ReactNode } from "react";

import { useMemo, useState } from "react";
import { TableBody } from "react-aria-components";

import { Pagination } from "@/components/pagination";
import { Switch } from "@/components/switch";
import { Cell, Column, Row, Table, TableHeader } from "@/components/table";
import { FeaturePageLayout } from "@/features/shared/layout";

import { useAddConfidenceCheckerTeam } from "./hooks/useAddConfidenceCheckerTeam";
import { useConfidenceCheckerAdminSettings } from "./hooks/useConfidenceCheckerAdminSettings";
import { useRemoveConfidenceCheckerTeam } from "./hooks/useRemoveConfidenceCheckerTeam";
import { useSetConfidenceCheckerDesignType } from "./hooks/useSetConfidenceCheckerDesignType";

const PAGE_SIZE = 10;

interface EnabledRow {
  id: number;
  isEnabled: boolean;
  name: string;
}

export function ConfidenceCheckerAdminPage() {
  const { data, isLoading } = useConfidenceCheckerAdminSettings();
  const addTeam = useAddConfidenceCheckerTeam();
  const removeTeam = useRemoveConfidenceCheckerTeam();
  const setDesignType = useSetConfidenceCheckerDesignType();

  const handleTeamToggle = (teamId: number, isSelected: boolean) => {
    if (isSelected) addTeam.mutate(teamId);
    else removeTeam.mutate(teamId);
  };

  const handleDesignTypeToggle = (designTypeId: number, enabled: boolean) => {
    setDesignType.mutate({ designTypeId, enabled });
  };

  return (
    <FeaturePageLayout isLoading={isLoading} title="DCC — Confidence Checker">
      <div className="flex w-full flex-col gap-8 p-6">
        <p className="text-sm text-text-secondary">
          Controls which teams and design types have the design confidence
          checker (DCC) enabled. A request only goes through DCC when its design
          type is enabled below <strong>and</strong> the designer&apos;s team is
          enabled below.
        </p>

        <EnabledTable
          emptyMessage="No teams found."
          nameHeader="Team"
          onToggle={handleTeamToggle}
          rows={data?.teams ?? []}
        />

        <EnabledTable
          emptyMessage="No design types found."
          nameHeader="Design type"
          onToggle={handleDesignTypeToggle}
          rows={data?.designTypes ?? []}
        />
      </div>
    </FeaturePageLayout>
  );
}

function EnabledTable({
  emptyMessage,
  nameHeader,
  onToggle,
  rows,
}: {
  emptyMessage: string;
  nameHeader: string;
  onToggle: (id: number, isSelected: boolean) => void;
  rows: EnabledRow[];
}): ReactNode {
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [page, rows],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-xl border border-border-secondary bg-bg-primary">
        <Table aria-label={nameHeader}>
          <TableHeader>
            <Column defaultWidth="3fr" id="name" isRowHeader minWidth={200}>
              {nameHeader}
            </Column>
            <Column defaultWidth="1fr" id="enabled" minWidth={140}>
              DCC enabled
            </Column>
          </TableHeader>
          <TableBody
            items={pageRows}
            renderEmptyState={() => (
              <p className="p-3xl text-sm text-text-tertiary">{emptyMessage}</p>
            )}
          >
            {(row) => (
              <Row>
                <Cell>{row.name}</Cell>
                <Cell>
                  <Switch
                    aria-label={`Toggle DCC for ${row.name}`}
                    isSelected={row.isEnabled}
                    onChange={(isSelected) => onToggle(row.id, isSelected)}
                  />
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      </div>

      {rows.length > PAGE_SIZE && (
        <Pagination
          currentPage={page}
          onPageChange={setPage}
          totalPages={totalPages}
        />
      )}
    </div>
  );
}
